import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UCPListContainer,
  Content,
  StickyHeaderWrapper,
  ListContent,
  LoaderWrapper,
  CardsList,
} from './UCPList.styled';
import UCPListHeader from '../../components/UCPListHeader';
import SaveSingleContactModal from '../../../../../elements/v2/SaveSingleContactModal/SaveSingleContactModal';
import Footer from '../../components/Footer';
import { ContactCard, ContactsEmptyState } from './components';
import { Alert, CircularProgress, WidgetEmptyErrorPlaceholder } from '@ann/web-components';
import ContactSlideOut from '../../components/ContactSlideout';
import { useUCPListPage } from '../../hooks/useUCPListPage';
import { useArchiveContact } from './hooks/useArchiveContact';

const UCPList = () => {
  const navigate = useNavigate();
  const [showContactSlideOut, setShowContactSlideOut] = useState(false);
  const [selectedContactUuid, setSelectedContactUuid] = useState(null);
  const [alert, setAlert] = useState(null);
  const [createContactModal, setCreateContactModal] = useState(false);

  const {
    contacts,
    meta,
    loading,
    error,
    currentPage,
    setCurrentPage,
    setItemsPerPage,
    searchTerm,
    setSearchTerm,
    contactTypeFilter,
    setContactTypeFilter,
    refreshContacts,
  } = useUCPListPage();

  const { isArchived, handleToggleArchive } = useArchiveContact(setAlert);

  const sortedContacts = useMemo(() => {
    return [...contacts].sort((a, b) => {
      const aArch = isArchived(a.partyUuid);
      const bArch = isArchived(b.partyUuid);
      if (aArch && !bArch) return 1;
      if (!aArch && bArch) return -1;
      return 0;
    });
  }, [contacts, isArchived]);

  const handleFiltersClick = useCallback(() => {
  }, []);

  const handleCreateContact = useCallback(() => {
    setCreateContactModal(true);
  }, []);

  const handlePreviewContact = useCallback((contactUuid) => {
    setSelectedContactUuid(contactUuid);
    setShowContactSlideOut(true);
  }, []);

  const handleCloseSlideOut = useCallback(() => {
    setShowContactSlideOut(false);
    setSelectedContactUuid(null);
  }, []);

  const handleNavigateToProfile = useCallback(
    (partyUuid) => {
      const contact = contacts.find((c) => c.partyUuid === partyUuid);
      navigate(`/v1/ucp/${partyUuid}/overview`, {
        state: { contactName: contact?.legalName },
      });
    },
    [contacts, navigate]
  );

  const showCards = !loading && !error && contacts.length > 0;

  return (
    <>
      <SaveSingleContactModal
        createContactModal={createContactModal}
        setCreateContactModal={setCreateContactModal}
      />
      <ContactSlideOut
        open={showContactSlideOut}
        onClose={handleCloseSlideOut}
        contactUuid={selectedContactUuid}
      />
      {alert && (
        <Alert
          title={alert.title}
          description={alert.message}
          severity={alert.severity}
          width={"692px"}
          top={"61px"}
          right={"16px"}
          autoClose={true}
          autoCloseDelay={5000}
          onClose={() => setAlert(null)}
        />
      )}
      <UCPListContainer>
        <Content>
          <StickyHeaderWrapper>            
            <UCPListHeader
              contactTypeFilter={contactTypeFilter}
              onContactTypeChange={setContactTypeFilter}
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              onFiltersClick={handleFiltersClick}
              onCreateContact={handleCreateContact}
            />
          </StickyHeaderWrapper>

          <ListContent>
            {loading && <LoaderWrapper><CircularProgress size="large" /></LoaderWrapper>}
            {error && !loading && (
              <WidgetEmptyErrorPlaceholder
                icon="error"
                title="Something went wrong"
                message="We couldn't load your contacts. Please try again."
                buttonText="Retry"
                showButton={true}
                onClick={refreshContacts}
              />
            )}
            {showCards && (
              <CardsList>
                {sortedContacts.map((contact) => (
                  <ContactCard
                    key={contact.partyUuid}
                    contact={contact}
                    role={contact.role}
                    company={contact.company ?? null}
                    nextBestAction={contact.nextBestAction ?? null}
                    activitySummary={contact.activitySummary ?? null}
                    statusTags={contact.statusTags ?? []}
                    isArchived={isArchived(contact.partyUuid)}
                    onPreview={handlePreviewContact}
                    onNavigate={handleNavigateToProfile}
                    onArchive={handleToggleArchive}
                    onEmail={contact.email ? (c) => window.open(`mailto:${c.email}`, '_blank') : undefined}
                    onCall={contact.phone ? (c) => window.open(`tel:${c.phone}`, '_self') : undefined}
                  />
                ))}
              </CardsList>
            )}

            {!loading && !error && contacts.length === 0 && (
              <ContactsEmptyState onCreateContact={handleCreateContact} />
            )}
          </ListContent>
        </Content>
      </UCPListContainer>

      {meta && (
        <Footer
          currentPage={currentPage}
          totalPages={meta.totalPages}
          itemsPerPage={meta.perPage}
          totalItems={meta.totalItems}
          onPageChange={setCurrentPage}
          disabled={loading}
          onItemsPerPageChange={setItemsPerPage}
        />
      )}
    </>
  );
};

export default UCPList; 